import { RefObject } from 'react'

const headers = {
  credentials: 'include',
  'Content-Type': 'application/json',
}

const get = async (url: string, args?: RequestInit | undefined, options?: { isFile: boolean }) => {
  const res = await fetch(url, { headers, ...args })

  let resBody
  try {
    if (options?.isFile) return res
    resBody = await res.json()
  } catch (e) {
    // sometimes GET requests don't return any payload - i.e. GET /api/forgot-password
    resBody = {
      detail: e,
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const post = async (url: string, body: object, args?: RequestInit | undefined, abortController?: RefObject<AbortController | undefined>) => {
  let controller
  if (abortController) {
    // if a previous abort controller is defined, abort the request
    if (abortController.current) {
      abortController.current.abort()
    }
    // assign the new abort controller to the passed in ref
    // this value should be passed into every request from that component
    // if it wants aborted requests
    // eslint-disable-next-line no-param-reassign
    abortController.current = new AbortController()
    controller = abortController.current
  }

  let res
  try {
    res = await fetch(url, {
      headers,
      method: 'POST',
      body: JSON.stringify(body),
      ...args,
      signal: controller?.signal,
    })
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'unknown error in request'
    return {
      ok: false,
      body: {},
      status: 500,
      statusText: errorMessage,
      aborted: controller?.signal.aborted || errorMessage === 'The user aborted a request.',
    }
  }

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
    aborted: controller?.signal.aborted,
  }

  return returnObject
}

const put = async (url: string, body: object | FormData, args?: RequestInit | undefined) => {
  const isFormData = body instanceof FormData

  const putCallHeaders: HeadersInit = {
    ...headers,
  }

  // need to delete explicitly so that browser can set correct boundary for form data calls (i.e. image upload)
  if (isFormData) {
    delete putCallHeaders['Content-Type']
  }

  const res = await fetch(url, {
    headers: putCallHeaders,
    method: 'PUT',
    body: isFormData ? body : JSON.stringify(body),
    ...args,
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const patch = async (url: string, body: object, args?: RequestInit | undefined) => {
  const res = await fetch(url, {
    headers,
    method: 'PATCH',
    body: JSON.stringify(body),
    ...args,
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

const del = async (url: string) => {
  const res = await fetch(url, {
    headers,
    method: 'DELETE',
  })

  let resBody
  try {
    resBody = await res.json()
  } catch (e) {
    if (!res.ok) {
      resBody = {
        detail: e,
      }
    }
  }

  const returnObject = {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    body: resBody,
  }

  return returnObject
}

interface PaginatedQueryData {
  filters?: { fieldName: string; operator: string; value: string | number }[]
  type?: 'and' | 'or'
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  detail?: boolean
  first?: number
  after?: number
}
/**
 * Use this function to make paginated POST requests to query endpoints that support pagination, e.g. /api/nodes/query and /api/folders/query. This function will return all results. For true pagination where UI interaction fetches the next page, use the post function directly.
 * @param endpoint - string: API endpoint
 * @param data - object: request body for the POST request
 * @returns Promise<Array> - Promise that is resolved or rejected containing all results from the paginated query
 */
const postPaginatedQuery = async (endpoint: string, data: PaginatedQueryData) => {
  const allResults: unknown[] = []
  const limit = data.first ? data.first : 100

  const fetchPage = async (offset: number): Promise<void> => {
    const requestBody = {
      ...data,
      first: limit,
      after: btoa(offset.toString()),
    }

    const response = await post(endpoint, requestBody)
    if (!response.ok) throw response
    const results = response.body.results ?? []
    allResults.push(...results)
    const hasNextPage = response.body.hasNextPage ?? false

    if (hasNextPage) {
      await fetchPage(offset + limit)
    }
  }

  await fetchPage(data.after ? data.after : 0)
  return allResults
}

const Api = {
  get,
  post,
  put,
  patch,
  del,
  postPaginatedQuery,
}

export default Api
